import { FileFormat } from "datasets/pages/tables/types/interfaces/ingest";
import {
  DataIngestionResponse,
  TabularProfileTaskMessage,
  type ValidationMessageGroup,
} from "datasets/pages/tables/types/interfaces/ingestion-job";
import FileInfo from "files/types/interfaces/fileInfo";
import { StartProfilingParams, ProfilingResult } from "./hooks/useStartProfiling";

// =============================================================================
// Constants
// =============================================================================

export const CARDINALITY_CATEGORY = {
  LOW: "low_cardinality",
  HIGH: "high_cardinality",
} as const;

// =============================================================================
// Re-exports (API types live in tables/types/interfaces/)
// =============================================================================

export type {
  IngestionJobType,
  IngestionJobStatus,
  IngestionStatus,
  IngestionJob,
} from "datasets/pages/tables/types/interfaces/ingestion-job";

// =============================================================================
// Component Props
// =============================================================================

export interface FileValidationModalProps {
  open: boolean;
  onClose: () => void;
  onFileValidated: (file: FileInfo, validationMetadata: ValidationMetadata) => void;
  /** Optional: called on the success path (Confirm & Submit). Sets validated=true in URL. */
  onConfirm?: () => void;
}

// =============================================================================
// UI Display Types (for rendering validation cards)
// =============================================================================

/** UI-friendly representation of a validation job for display */
export interface ValidationJob {
  title: string;
  description: string;
  result: string;
  status: "passed" | "warning" | "running" | "failed";
  details?: string;
  error?: string;
  messages?: string[];
  messageGroups?: ValidationMessageGroup[];
}

export interface ValidationState {
  jobs: ValidationJob[];
  summary: ValidationSummary;
  validatedFilePath?: string;
  /** S3 URI of the _INVALID_ROWS file (rows that failed validation) */
  invalidRowsFilePath?: string;
  /** S3 URI of the _ROW_DETAILS.json file (per-row error reasons) */
  rowDetailsFilePath?: string;
  /** Detected file type from validation (e.g. "csv", "tsv"), used to derive delimiter */
  fileType?: string;
  isComplete: boolean;
  hasErrors: boolean;
  hasWarnings: boolean;
}

export interface ValidationSummary {
  totalRows: number;
  headerRow: boolean;
  dataRows: number;
  errorRows: number;
  cleanRows: number;
}

export interface FileUploadState {
  selectedFile: FileInfo | null;
  uploadProgress?: number;
  isUploading: boolean;
}

export interface ColumnProfileDisplay {
  name: string;
  type: string;
  categorical: boolean;
  nullPercent: number;
  // sampleValues?: string[];
  // validationErrors?: string[];
}

export interface DataProfile {
  columns: ColumnProfileDisplay[];
  statistics: {
    totalColumns: number;
    categoricalColumns: number;
    goodRows: number;
    errorRows: number;
  };
  metadata?: {
    processingTime?: number;
    dataQualityScore?: number;
  };
}

export function mapTabularProfileToDataProfile(
  profile: TabularProfileTaskMessage,
  errorRows: number = 0
): DataProfile {
  const columns: ColumnProfileDisplay[] = profile.column_profile.map((col) => ({
    name: col.column_name,
    type: col.data_type,
    categorical: col.cardinality.cardinality_category === CARDINALITY_CATEGORY.LOW,
    nullPercent: profile.num_rows > 0 ? (col.null_count / profile.num_rows) * 100 : 0,
  }));

  const categoricalColumns = columns.filter((c) => c.categorical).length;

  return {
    columns,
    statistics: {
      totalColumns: profile.num_columns,
      categoricalColumns,
      goodRows: profile.num_rows,
      errorRows: errorRows,
    },
  };
}

export interface ValidationMetadata {
  validatedFilePath?: string;
  summary?: ValidationSummary;
  dataProfile?: DataProfile;
  file_format?: FileFormat;
}

// =============================================================================
// Stepper State (shared shape for StepperContext consumers)
// =============================================================================

/** Return type from the startValidation hook */
export interface ValidationResult {
  ingestionId: string;
  validationJobId: string;
  profilingJobId?: string;
}

/**
 * Typed shape of the stepper state used by the file validation modal.
 *
 * StepperStateSync writes this into the stepper context and all child
 * components (ValidationNavigation, ValidationSteps, etc.) read from it
 * via `useStepperContext<FileValidationStepperState>()`.
 */
export interface FileValidationStepperState {
  validationState: ValidationState;
  dataProfile: DataProfile | null;
  profilingError: ProfilingError | null;
  isValidating: boolean;
  isProfiling: boolean;
  startValidation: (params: { sourceFile: string }) => Promise<ValidationResult | null>;
  startProfiling: (params: StartProfilingParams) => Promise<ProfilingResult | null>;
  ingestion: DataIngestionResponse | undefined;
  isLoadingIngestion: boolean;
  recoveredSourceFile: string | null;
}

// =============================================================================
// Additional types for better type safety
// =============================================================================

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface ValidationError {
  code: string;
  message: string;
  details?: any;
}

export interface ProfilingError {
  message: string;
  timestamp: string;
  retryable: boolean;
}
